import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Collapse, Container, Form, Modal, Row, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FaEye, FaTimesCircle, FaSave, FaChevronUp, FaChevronDown, FaTrash, FaPlus, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { apiClient } from '@/services/apiClient';
import TemplatePreviewModal, { TemplatePreviewData } from '@/components/TemplatePreviewModal';

type TemplateStep = {
  stepName: string;
  stepDescription: string;
  stepConfig: string;
  sortOrder: number;
  isRequired: boolean;
  isActive: boolean;
};

type TemplateDetail = {
  templateGuid: string;
  templateName: string;
  templateCategory: string;
  templateDescription?: string;
  reviewTemplateConfig?: string;
  isActive: boolean;
  activeReviewCount: number;
  steps: TemplateStep[];
};

type ReportDefinition = {
  m365ReportId: number;
  reportTitle: string;
  reportOptions?: string;
  isActive: boolean;
};

const emptyStep = (sortOrder: number): TemplateStep => ({
  stepName: '',
  stepDescription: '',
  stepConfig: '',
  sortOrder,
  isRequired: true,
  isActive: true
});

const ReviewTemplateEditor: React.FC = () => {
  const { templateGuid } = useParams<{ templateGuid: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = Boolean(templateGuid);

  // ---- loading / saving flags ----
  const [loading, setLoading] = useState(isEdit);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [savingStepIdx, setSavingStepIdx] = useState<number | null>(null);
  const [orderSaving, setOrderSaving] = useState(false);

  // ---- error / success ----
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  // ---- toast ----
  const [toastMsg, setToastMsg] = useState('');
  const [toastVariant, setToastVariant] = useState<'success' | 'danger'>('success');
  const [showToast, setShowToast] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  // ---- preview ----
  const [showPreview, setShowPreview] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  // ---- template header ----
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [reviewTemplateConfig, setReviewTemplateConfig] = useState('');
  const [finalReviewAssigneeEmail, setFinalReviewAssigneeEmail] = useState('');
  const [finalReviewRole, setFinalReviewRole] = useState('');
  const [reportCatalogConfig, setReportCatalogConfig] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [availableReports, setAvailableReports] = useState<ReportDefinition[]>([]);
  const [selectedCatalogReportId, setSelectedCatalogReportId] = useState('');
  const [selectedCatalogReportKey, setSelectedCatalogReportKey] = useState('');
  const [catalogAutoQueueOnAssign, setCatalogAutoQueueOnAssign] = useState(false);
  const [escalationTriggerDecision, setEscalationTriggerDecision] = useState('Further review required');
  const [escalationFollowUpTemplateGuid, setEscalationFollowUpTemplateGuid] = useState('');
  const [escalationFollowUpReviewName, setEscalationFollowUpReviewName] = useState('Records Content Review');
  const [escalationAssigneeEmail, setEscalationAssigneeEmail] = useState('');
  const [escalationAssigneeRole, setEscalationAssigneeRole] = useState('');
  const [escalationNotificationGroupId, setEscalationNotificationGroupId] = useState('');
  const [escalationNotificationGroupName, setEscalationNotificationGroupName] = useState('');
  const [escalationStatusOnEscalation, setEscalationStatusOnEscalation] = useState('Escalated');
  const [isActive, setIsActive] = useState(true);
  const [activeReviewCount, setActiveReviewCount] = useState(0);

  // ---- steps: `steps` = editing state; `savedSteps` = last committed to server ----
  const [steps, setSteps] = useState<TemplateStep[]>([emptyStep(1)]);
  const [savedSteps, setSavedSteps] = useState<TemplateStep[]>([emptyStep(1)]);
  const [expandedSteps, setExpandedSteps] = useState<boolean[]>([false]);

  // ---- load (edit mode) ----
  useEffect(() => {
    if (!isEdit || !templateGuid) return;

    const load = async () => {
      try {
        setLoading(true);
        setDetailsError(null);
        const { data } = await apiClient.get<TemplateDetail>(`/api/admin/reviewmanagement/templates/${templateGuid}`);

        setTemplateName(data.templateName ?? '');
        setTemplateCategory(data.templateCategory ?? '');
        setTemplateDescription(data.templateDescription ?? '');
        setReviewTemplateConfig(data.reviewTemplateConfig ?? '');

        const initialFinalReview = extractFinalReviewConfig(data.reviewTemplateConfig ?? '');
        setFinalReviewAssigneeEmail(initialFinalReview.assigneeEmail);
        setFinalReviewRole(initialFinalReview.role);

        const initialReportCatalog = extractReportCatalogConfig(data.reviewTemplateConfig ?? '');
        setReportCatalogConfig(initialReportCatalog);

        const initialEscalation = extractEscalationConfig(data.reviewTemplateConfig ?? '');
        setEscalationTriggerDecision(initialEscalation.triggerDecision);
        setEscalationFollowUpTemplateGuid(initialEscalation.followUpTemplateGuid);
        setEscalationFollowUpReviewName(initialEscalation.followUpReviewName);
        setEscalationAssigneeEmail(initialEscalation.assigneeEmail);
        setEscalationAssigneeRole(initialEscalation.assigneeRole);
        setEscalationNotificationGroupId(initialEscalation.notificationGroupId);
        setEscalationNotificationGroupName(initialEscalation.notificationGroupName);
        setEscalationStatusOnEscalation(initialEscalation.statusOnEscalation);

        setIsActive(data.isActive);
        setActiveReviewCount(data.activeReviewCount ?? 0);

        const loaded = (data.steps ?? []).map((s, i) => ({ ...s, sortOrder: i + 1 }));
        const init = loaded.length > 0 ? loaded : [emptyStep(1)];
        setSteps(init);
        setSavedSteps(init);
        setExpandedSteps(init.map(() => false));
      } catch (err: any) {
        setDetailsError(err?.response?.data?.error ?? err?.message ?? 'Failed to load review template');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isEdit, templateGuid]);

  useEffect(() => {
    const loadReports = async () => {
      try {
        const { data } = await apiClient.get<ReportDefinition[]>('/api/admin/reports');
        const active = (data ?? []).filter((r) => r?.isActive !== false);
        setAvailableReports(active);
      } catch {
        setAvailableReports([]);
      }
    };

    loadReports();
  }, []);

  // ---- helpers ----
  const showToastFn = (msg: string, variant: 'success' | 'danger' = 'success') => {
    setToastMsg(msg);
    setToastVariant(variant);
    setShowToast(true);
  };

  const slugifyCatalogKey = (value: string): string => {
    const slug = value
      .toLowerCase()
      .replace(/[^a-z0-9]+(.)/g, (_match, chr: string) => chr.toUpperCase())
      .replace(/[^a-zA-Z0-9]/g, '');

    if (!slug) return 'reportKey';
    return slug.charAt(0).toLowerCase() + slug.slice(1);
  };

  const parseCatalogObject = (): Record<string, unknown> => {
    const trimmed = reportCatalogConfig.trim();
    if (!trimmed) return {};

    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Report catalog must be a JSON object.');
    }

    return parsed as Record<string, unknown>;
  };

  const addSelectedReportToCatalog = () => {
    const reportId = Number(selectedCatalogReportId);
    if (!Number.isFinite(reportId) || reportId <= 0) {
      showToastFn('Select a report to add to catalog.', 'danger');
      return;
    }

    const report = availableReports.find((r) => r.m365ReportId === reportId);
    if (!report) {
      showToastFn('Selected report is not available.', 'danger');
      return;
    }

    const key = selectedCatalogReportKey.trim() || slugifyCatalogKey(report.reportTitle);

    try {
      const catalog = parseCatalogObject();

      let parsedDefaultOptions: unknown = undefined;
      if (report.reportOptions && report.reportOptions.trim()) {
        try {
          parsedDefaultOptions = JSON.parse(report.reportOptions);
        } catch {
          parsedDefaultOptions = undefined;
        }
      }

      catalog[key] = {
        m365ReportId: report.m365ReportId,
        title: report.reportTitle,
        ...(catalogAutoQueueOnAssign ? { autoQueueOnAssign: true } : {}),
        ...(parsedDefaultOptions ? { defaultOptions: parsedDefaultOptions } : {})
      };

      setReportCatalogConfig(JSON.stringify(catalog, null, 2));
      setSelectedCatalogReportKey('');
      setCatalogAutoQueueOnAssign(false);
      showToastFn(`Added ${report.reportTitle} as ${key}.`);
      setShowAdvanced(true);
    } catch (err: any) {
      showToastFn(err?.message ?? 'Report catalog JSON is invalid.', 'danger');
    }
  };

  const extractFinalReviewConfig = (rawConfig?: string | null): { assigneeEmail: string; role: string } => {
    if (!rawConfig || !rawConfig.trim()) {
      return { assigneeEmail: '', role: '' };
    }

    try {
      const parsed = JSON.parse(rawConfig) as Record<string, unknown>;
      const finalReview = parsed?.finalReview as Record<string, unknown> | undefined;
      const assigneeEmail = typeof finalReview?.assigneeEmail === 'string' ? finalReview.assigneeEmail.trim() : '';
      const role = typeof finalReview?.role === 'string' ? finalReview.role.trim() : '';
      return { assigneeEmail, role };
    } catch {
      return { assigneeEmail: '', role: '' };
    }
  };

  const extractEscalationConfig = (rawConfig?: string | null): {
    triggerDecision: string;
    followUpTemplateGuid: string;
    followUpReviewName: string;
    assigneeEmail: string;
    assigneeRole: string;
    notificationGroupId: string;
    notificationGroupName: string;
    statusOnEscalation: string;
  } => {
    const defaults = {
      triggerDecision: 'Further review required',
      followUpTemplateGuid: '',
      followUpReviewName: 'Records Content Review',
      assigneeEmail: '',
      assigneeRole: '',
      notificationGroupId: '',
      notificationGroupName: '',
      statusOnEscalation: 'Escalated',
    };

    if (!rawConfig || !rawConfig.trim()) {
      return defaults;
    }

    try {
      const parsed = JSON.parse(rawConfig) as Record<string, unknown>;
      const escalation = parsed?.escalation as Record<string, unknown> | undefined;

      return {
        triggerDecision: typeof escalation?.triggerDecision === 'string' && escalation.triggerDecision.trim()
          ? escalation.triggerDecision.trim()
          : defaults.triggerDecision,
        followUpTemplateGuid: typeof escalation?.followUpTemplateGuid === 'string'
          ? escalation.followUpTemplateGuid.trim()
          : defaults.followUpTemplateGuid,
        followUpReviewName: typeof escalation?.followUpReviewName === 'string' && escalation.followUpReviewName.trim()
          ? escalation.followUpReviewName.trim()
          : defaults.followUpReviewName,
        assigneeEmail: typeof escalation?.assigneeEmail === 'string'
          ? escalation.assigneeEmail.trim()
          : defaults.assigneeEmail,
        assigneeRole: typeof escalation?.assigneeRole === 'string'
          ? escalation.assigneeRole.trim()
          : defaults.assigneeRole,
        notificationGroupId: typeof escalation?.notificationGroupId === 'string'
          ? escalation.notificationGroupId.trim()
          : defaults.notificationGroupId,
        notificationGroupName: typeof escalation?.notificationGroupName === 'string'
          ? escalation.notificationGroupName.trim()
          : defaults.notificationGroupName,
        statusOnEscalation: typeof escalation?.statusOnEscalation === 'string' && escalation.statusOnEscalation.trim()
          ? escalation.statusOnEscalation.trim()
          : defaults.statusOnEscalation,
      };
    } catch {
      return defaults;
    }
  };

  const extractReportCatalogConfig = (rawConfig?: string | null): string => {
    if (!rawConfig || !rawConfig.trim()) {
      return '';
    }

    try {
      const parsed = JSON.parse(rawConfig) as Record<string, unknown>;
      const reportCatalog = parsed?.reportCatalog;
      if (!reportCatalog || typeof reportCatalog !== 'object' || Array.isArray(reportCatalog)) {
        return '';
      }

      return JSON.stringify(reportCatalog, null, 2);
    } catch {
      return '';
    }
  };

  useEffect(() => {
    const hasAdvancedValues = Boolean(
      reportCatalogConfig.trim() ||
      escalationFollowUpTemplateGuid.trim() ||
      escalationAssigneeEmail.trim() ||
      escalationAssigneeRole.trim() ||
      escalationNotificationGroupId.trim() ||
      reviewTemplateConfig.trim()
    );

    if (hasAdvancedValues) {
      setShowAdvanced(true);
    }
  }, [
    reportCatalogConfig,
    escalationFollowUpTemplateGuid,
    escalationAssigneeEmail,
    escalationAssigneeRole,
    escalationNotificationGroupId,
    reviewTemplateConfig
  ]);

  const composeReviewTemplateConfig = (): string | null => {
    const trimmedRaw = reviewTemplateConfig.trim();
    const trimmedReportCatalog = reportCatalogConfig.trim();
    const hasStructuredFinalReview = Boolean(finalReviewAssigneeEmail.trim() || finalReviewRole.trim());
    const hasStructuredEscalation = Boolean(
      escalationFollowUpTemplateGuid.trim() ||
      escalationAssigneeEmail.trim() ||
      escalationAssigneeRole.trim() ||
      escalationNotificationGroupId.trim()
    );
    const hasStructuredReportCatalog = Boolean(trimmedReportCatalog);

    if (!trimmedRaw && !hasStructuredFinalReview && !hasStructuredEscalation && !hasStructuredReportCatalog) {
      return null;
    }

    let configObject: Record<string, unknown> = {};
    if (trimmedRaw) {
      try {
        const parsed = JSON.parse(trimmedRaw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Template config must be a JSON object.');
        }
        configObject = parsed as Record<string, unknown>;
      } catch {
        throw new Error('Template config JSON is invalid. Fix it before saving, or clear it and use the structured fields.');
      }
    }

    const existingFinalReview = (
      configObject.finalReview && typeof configObject.finalReview === 'object' && !Array.isArray(configObject.finalReview)
    )
      ? { ...(configObject.finalReview as Record<string, unknown>) }
      : {};

    const assignee = finalReviewAssigneeEmail.trim().toLowerCase();
    const role = finalReviewRole.trim();

    if (assignee) {
      existingFinalReview.assigneeEmail = assignee;
    } else {
      delete existingFinalReview.assigneeEmail;
    }

    if (role) {
      existingFinalReview.role = role;
    } else {
      delete existingFinalReview.role;
    }

    if (Object.keys(existingFinalReview).length > 0) {
      configObject.finalReview = existingFinalReview;
    } else {
      delete configObject.finalReview;
    }

    if (trimmedReportCatalog) {
      try {
        const reportCatalogParsed = JSON.parse(trimmedReportCatalog);
        if (!reportCatalogParsed || typeof reportCatalogParsed !== 'object' || Array.isArray(reportCatalogParsed)) {
          throw new Error('Report catalog must be a JSON object.');
        }
        configObject.reportCatalog = reportCatalogParsed;
      } catch {
        throw new Error('Report catalog JSON is invalid. It must be a JSON object keyed by report alias.');
      }
    } else {
      delete configObject.reportCatalog;
    }

    const existingEscalation = (
      configObject.escalation && typeof configObject.escalation === 'object' && !Array.isArray(configObject.escalation)
    )
      ? { ...(configObject.escalation as Record<string, unknown>) }
      : {};
    const hadExistingEscalation = Object.keys(existingEscalation).length > 0;
    const shouldPersistEscalation = hasStructuredEscalation || hadExistingEscalation;

    const triggerDecision = escalationTriggerDecision.trim();
    const followUpTemplateGuid = escalationFollowUpTemplateGuid.trim();
    const followUpReviewName = escalationFollowUpReviewName.trim();
    const escalationAssignee = escalationAssigneeEmail.trim().toLowerCase();
    const assigneeRole = escalationAssigneeRole.trim();
    const notificationGroupId = escalationNotificationGroupId.trim();
    const notificationGroupName = escalationNotificationGroupName.trim();
    const statusOnEscalation = escalationStatusOnEscalation.trim();

    if (shouldPersistEscalation) {
      if (triggerDecision) {
        existingEscalation.triggerDecision = triggerDecision;
      } else {
        delete existingEscalation.triggerDecision;
      }

      if (followUpTemplateGuid) {
        existingEscalation.followUpTemplateGuid = followUpTemplateGuid;
      } else {
        delete existingEscalation.followUpTemplateGuid;
      }

      if (followUpReviewName) {
        existingEscalation.followUpReviewName = followUpReviewName;
      } else {
        delete existingEscalation.followUpReviewName;
      }

      if (escalationAssignee) {
        existingEscalation.assigneeEmail = escalationAssignee;
      } else {
        delete existingEscalation.assigneeEmail;
      }

      if (assigneeRole) {
        existingEscalation.assigneeRole = assigneeRole;
      } else {
        delete existingEscalation.assigneeRole;
      }

      if (notificationGroupId) {
        existingEscalation.notificationGroupId = notificationGroupId;
      } else {
        delete existingEscalation.notificationGroupId;
      }

      if (notificationGroupName) {
        existingEscalation.notificationGroupName = notificationGroupName;
      } else {
        delete existingEscalation.notificationGroupName;
      }

      if (statusOnEscalation) {
        existingEscalation.statusOnEscalation = statusOnEscalation;
      } else {
        delete existingEscalation.statusOnEscalation;
      }

      if (Object.keys(existingEscalation).length > 0) {
        configObject.escalation = existingEscalation;
      } else {
        delete configObject.escalation;
      }
    } else {
      delete configObject.escalation;
    }

    return Object.keys(configObject).length > 0 ? JSON.stringify(configObject) : null;
  };

  const buildPayload = (currentSteps: TemplateStep[], actionDescription?: string, overrideIsActive?: boolean) => ({
    templateName: templateName.trim(),
    templateCategory: templateCategory.trim(),
    templateDescription: templateDescription.trim() || null,
    reviewTemplateConfig: composeReviewTemplateConfig(),
    isActive: overrideIsActive ?? isActive,
    steps: currentSteps.map((s, i) => ({
      ...s,
      stepName: s.stepName.trim(),
      stepDescription: s.stepDescription?.trim() || null,
      stepConfig: s.stepConfig?.trim() || null,
      sortOrder: i + 1,
    })),
    sessionGuid: localStorage.getItem('sessionGuid'),
    actionDescription: actionDescription ?? null,
  });

  // A step is dirty when its current value differs from the last saved value.
  // Steps beyond savedSteps.length are new (unsaved) and always dirty.
  const isDirtyStep = (i: number): boolean => {
    if (i >= savedSteps.length) return true;
    const s = steps[i];
    const saved = savedSteps[i];
    return (
      s.stepName !== saved.stepName ||
      (s.stepDescription ?? '') !== (saved.stepDescription ?? '') ||
      (s.stepConfig ?? '') !== (saved.stepConfig ?? '') ||
      s.isRequired !== saved.isRequired ||
      s.isActive !== saved.isActive
    );
  };

  const hasDirtySteps = () => steps.some((_, i) => isDirtyStep(i));

  const isAnySaving = detailsSaving || savingStepIdx !== null || orderSaving;

  // ---- step field edit ----
  const updateStep = (index: number, patch: Partial<TemplateStep>) => {
    setSteps((cur) => cur.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const tryParseStepConfigObject = (raw?: string): Record<string, unknown> => {
    if (!raw || !raw.trim()) {
      return {};
    }

    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {};
      }
      return parsed as Record<string, unknown>;
    } catch {
      return {};
    }
  };

  const setDecisionStepPreset = (index: number) => {
    const current = tryParseStepConfigObject(steps[index]?.stepConfig);
    const next = {
      ...current,
      decisions: Array.isArray(current.decisions) ? current.decisions : ['Approve', 'Further review required'],
      decisionNoteRequired: current.decisionNoteRequired === true,
      decisionNoteEnabled: current.decisionNoteEnabled !== false
    };

    delete (next as Record<string, unknown>).type;
    updateStep(index, { stepConfig: JSON.stringify(next, null, 2) });
  };

  const setReportTriggerStepPreset = (index: number) => {
    const current = tryParseStepConfigObject(steps[index]?.stepConfig);
    const next = {
      ...current,
      type: 'reportTrigger',
      reports: Array.isArray(current.reports) ? current.reports : ['siteInventory', 'userAccess', 'sharingLinks'],
      allowRegenerate: current.allowRegenerate === true,
      includeParentReports: current.includeParentReports !== false,
      sharingMode: typeof current.sharingMode === 'string' ? current.sharingMode : 'readOnly'
    };

    updateStep(index, { stepConfig: JSON.stringify(next, null, 2) });
  };

  const setReportTriggerKeys = (index: number, keysCsv: string) => {
    const current = tryParseStepConfigObject(steps[index]?.stepConfig);
    const keys = keysCsv
      .split(',')
      .map((v) => v.trim())
      .filter((v, i, arr) => !!v && arr.indexOf(v) === i);

    const next = {
      ...current,
      type: 'reportTrigger',
      reports: keys,
      allowRegenerate: current.allowRegenerate === true,
      includeParentReports: current.includeParentReports !== false,
      sharingMode: typeof current.sharingMode === 'string' ? current.sharingMode : 'readOnly'
    };

    updateStep(index, { stepConfig: JSON.stringify(next, null, 2) });
  };

  const toggleStepExpanded = (index: number) => {
    setExpandedSteps((cur) => cur.map((isOpen, i) => (i === index ? !isOpen : isOpen)));
  };

  const expandAllSteps = () => {
    setExpandedSteps(steps.map(() => true));
  };

  const collapseAllSteps = () => {
    setExpandedSteps(steps.map(() => false));
  };

  // Cancel: revert to saved value, or remove if it was a brand-new unsaved step
  const cancelStepEdit = (index: number) => {
    if (index < savedSteps.length) {
      setSteps((cur) => cur.map((s, i) => (i === index ? { ...savedSteps[index] } : s)));
    } else {
      const trimmed = steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, sortOrder: i + 1 }));
      setSteps(trimmed);
      setExpandedSteps((cur) => cur.filter((_, i) => i !== index));
    }
  };

  // Save one step (calls PUT with full current state)
  const saveStepEdit = async (index: number) => {
    if (!isEdit || !templateGuid) return;
    if (!steps[index].stepName.trim()) {
      showToastFn('Step name is required.', 'danger');
      return;
    }
    try {
      setSavingStepIdx(index);
        const stepName = steps[index].stepName.trim();
        await apiClient.put(`/api/admin/reviewmanagement/templates/${templateGuid}`, buildPayload(steps, `Edited step ${index + 1}: '${stepName}'`));
      setSavedSteps([...steps].map((s, i) => ({ ...s, sortOrder: i + 1 })));
      showToastFn('Step saved.');
    } catch (err: any) {
      showToastFn(err?.response?.data?.error ?? err?.message ?? 'Failed to save step.', 'danger');
    } finally {
      setSavingStepIdx(null);
    }
  };

  // ---- reorder (auto-saves) ----
  const moveStep = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= steps.length) return;
    if (hasDirtySteps()) {
      showToastFn('Save or cancel unsaved step changes before reordering.', 'danger');
      return;
    }

    const previousSteps = [...steps];
    const previousExpanded = [...expandedSteps];

    const copy = [...steps];
    [copy[index], copy[target]] = [copy[target], copy[index]];
    const reordered = copy.map((s, i) => ({ ...s, sortOrder: i + 1 }));
    setSteps(reordered);

    const reorderedExpanded = [...previousExpanded];
    [reorderedExpanded[index], reorderedExpanded[target]] = [reorderedExpanded[target], reorderedExpanded[index]];
    setExpandedSteps(reorderedExpanded);

    if (!isEdit || !templateGuid) return;

    try {
      setOrderSaving(true);
        const movedName = steps[index].stepName.trim();
        const dirLabel = direction === 1 ? 'down' : 'up';
        await apiClient.put(`/api/admin/reviewmanagement/templates/${templateGuid}`, buildPayload(reordered, `Moved step '${movedName}' ${dirLabel} to position ${target + 1}`));
      setSavedSteps(reordered);
      showToastFn('Step order updated.');
    } catch (err: any) {
      setSteps(previousSteps); // revert
      setExpandedSteps(previousExpanded);
      showToastFn('Failed to update step order.', 'danger');
    } finally {
      setOrderSaving(false);
    }
  };

  // ---- add step ----
  const addStep = () => {
    setSteps((cur) => [...cur, emptyStep(cur.length + 1)]);
    setExpandedSteps((cur) => [...cur, false]);
    // New step is intentionally NOT added to savedSteps until the user saves it
  };

  // ---- add standard final review steps ----
  const FINAL_APPROVAL_STEP_NAME = 'Complete Final Approval';
  const FINAL_OUTCOME_STEP_NAME = 'Record Final Outcome';

  const hasFinalApprovalStep = steps.some(
    (s) => s.stepName.trim().toLowerCase() === FINAL_APPROVAL_STEP_NAME.toLowerCase()
  );
  const hasFinalOutcomeStep = steps.some(
    (s) => s.stepName.trim().toLowerCase() === FINAL_OUTCOME_STEP_NAME.toLowerCase()
  );
  const finalReviewConfigured = Boolean(finalReviewAssigneeEmail.trim() || finalReviewRole.trim());
  const missingFinalSteps = finalReviewConfigured && (!hasFinalApprovalStep || !hasFinalOutcomeStep);

  const addFinalReviewSteps = () => {
    const toAdd: TemplateStep[] = [];
    if (!hasFinalApprovalStep) {
      toAdd.push({
        stepName: FINAL_APPROVAL_STEP_NAME,
        stepDescription: 'Record final reviewer approval and confirm all required review actions are complete.',
        stepConfig: '',
        sortOrder: 0, // recalculated below
        isRequired: true,
        isActive: true,
      });
    }
    if (!hasFinalOutcomeStep) {
      toAdd.push({
        stepName: FINAL_OUTCOME_STEP_NAME,
        stepDescription: 'Capture and confirm the final outcome for audit and reporting.',
        stepConfig: '{"type":"decision","label":"Final Outcome","options":["Completed"]}',
        sortOrder: 0,
        isRequired: true,
        isActive: true,
      });
    }
    if (toAdd.length === 0) return;
    setSteps((cur) => [
      ...cur,
      ...toAdd.map((s, i) => ({ ...s, sortOrder: cur.length + i + 1 })),
    ]);
    setExpandedSteps((cur) => [...cur, ...toAdd.map(() => true)]);
  };

  // ---- remove step (auto-saves in edit mode) ----
  const removeStep = async (index: number) => {
    if (steps.length <= 1) return;

    const isNewUnsaved = index >= savedSteps.length;
    const previousSteps = [...steps];
    const previousExpanded = [...expandedSteps];
    const newSteps = steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, sortOrder: i + 1 }));
    setSteps(newSteps);
    setExpandedSteps((cur) => cur.filter((_, i) => i !== index));

    if (isNewUnsaved) return; // nothing on the server to remove

    if (!isEdit || !templateGuid) {
      setSavedSteps(newSteps);
      return;
    }

    try {
      const removedName = steps[index].stepName.trim();
      await apiClient.put(`/api/admin/reviewmanagement/templates/${templateGuid}`, buildPayload(newSteps, `Removed step '${removedName}'`));
      setSavedSteps(newSteps);
      showToastFn('Step removed.');
    } catch (err: any) {
      setSteps(previousSteps); // revert
      setExpandedSteps(previousExpanded);
      showToastFn('Failed to remove step.', 'danger');
    }
  };

  // ---- save template details (edit mode) ----
  const persistDetails = async (overrideIsActive?: boolean) => {
    if (!templateName.trim() || !templateCategory.trim() || !isEdit || !templateGuid) return;

    try {
      setDetailsSaving(true);
      setDetailsError(null);
        await apiClient.put(`/api/admin/reviewmanagement/templates/${templateGuid}`, buildPayload(steps, 'Updated template details', overrideIsActive));
      setSavedSteps([...steps].map((s, i) => ({ ...s, sortOrder: i + 1 })));
      showToastFn('Template details saved.');
    } catch (err: any) {
      setDetailsError(err?.response?.data?.error ?? err?.message ?? 'Failed to save template details.');
    } finally {
      setDetailsSaving(false);
    }
  };

  const saveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    await persistDetails();
  };

  const handleToggleTemplateActive = async () => {
    if (isActive && activeReviewCount > 0) {
      setShowDisableConfirm(true);
      return;
    }

    const nextIsActive = !isActive;
    setIsActive(nextIsActive);
    await persistDetails(nextIsActive);
  };

  const confirmDisableTemplate = async () => {
    setShowDisableConfirm(false);
    setIsActive(false);
    await persistDetails(false);
  };

  // ---- preview ----
  const handlePreviewClick = () => {
    if (hasDirtySteps() || (isEdit && isDirtyTemplate())) {
      setShowUnsavedWarning(true);
    } else {
      setShowPreview(true);
    }
  };

  const isDirtyTemplate = (): boolean => {
    if (!isEdit) return false;
    // Check if template details have changed (simplified check)
    return false; // Implement if needed for details tracking
  };

  const proceedWithPreview = () => {
    setShowUnsavedWarning(false);
    setShowPreview(true);
  };

  // Auto-open preview when navigated here with ?preview=true
  useEffect(() => {
    if (searchParams.get('preview') === 'true' && !loading) {
      setShowPreview(true);
    }
  }, [loading, searchParams]);

  const getPreviewData = (): TemplatePreviewData => {
    return {
      templateName: templateName.trim() || '(Untitled)',
      templateCategory: templateCategory.trim() || '(Uncategorized)',
      templateDescription: templateDescription.trim() || undefined,
      totalSteps: steps.length,
      requiredSteps: steps.filter((s) => s.isRequired).length,
      templateGuid: templateGuid,
      steps: steps.map((s) => {
        let parsedConfig: Record<string, unknown> | undefined;
        try {
          if (s.stepConfig?.trim()) {
            const p = JSON.parse(s.stepConfig);
            if (p && typeof p === 'object' && !Array.isArray(p)) {
              parsedConfig = p as Record<string, unknown>;
            }
          }
        } catch { /* ignore */ }
        return {
          stepName: (s.stepName ?? '').trim() || '(Untitled step)',
          stepDescription: (s.stepDescription ?? '').trim() || '',
          isRequired: s.isRequired,
          isActive: s.isActive,
          stepConfig: parsedConfig,
        };
      }),
    };
  };

  // ---- create (create mode) ----
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateName.trim() || !templateCategory.trim()) return;
    if (steps.length === 0 || steps.some((s) => !s.stepName.trim())) {
      setDetailsError('Each step requires a name.');
      return;
    }
    try {
      setDetailsSaving(true);
      setDetailsError(null);
      await apiClient.post('/api/admin/reviewmanagement/templates', buildPayload(steps));
      setCreateSuccess(true);
      setTimeout(() => navigate('/admin/assign-review'), 1000);
    } catch (err: any) {
      setDetailsError(err?.response?.data?.error ?? err?.message ?? 'Failed to create template.');
    } finally {
      setDetailsSaving(false);
    }
  };

  // ---- render ----
  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading template...</p>
      </Container>
    );
  }

  if (createSuccess) {
    return (
      <Container className="mt-4">
        <Alert variant="success">Template created successfully! Redirecting…</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Toast */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide bg={toastVariant}>
          <Toast.Body className="text-white fw-semibold">{toastMsg}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h2>{isEdit ? 'Edit Review Template' : 'Add Review Template'}</h2>
          <p className="text-muted mb-0">
            {isEdit
              ? 'Template details save with their own button. Step edits save inline. Reorder auto-saves.'
              : 'Create a reusable checklist template for review assignments.'}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button 
            variant="outline-info" 
            onClick={handlePreviewClick} 
            disabled={isAnySaving || !templateName.trim()}
          >
            <FaEye className="me-2" />
            Preview
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate('/admin/assign-review')} disabled={isAnySaving}>
            Back
          </Button>
        </div>
      </div>

      {activeReviewCount > 0 && (
        <Alert variant="warning">
          This template has {activeReviewCount} active in-progress review(s). Changes here affect future assignments only.
        </Alert>
      )}

      {detailsError && (
        <Alert variant="danger" dismissible onClose={() => setDetailsError(null)}>
          {detailsError}
        </Alert>
      )}

      {/* ---- Template Details Card ---- */}
      <Form onSubmit={isEdit ? saveDetails : handleCreate}>
        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <strong>Template Details</strong>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowAdvanced((s) => !s)}
                disabled={isAnySaving}
              >
                {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
              </Button>
              {isEdit && (
                <Button
                  type="button"
                  variant={isActive ? 'outline-danger' : 'outline-success'}
                  size="sm"
                  onClick={handleToggleTemplateActive}
                  disabled={isAnySaving}
                >
                  {isActive ? 'Disable Template' : 'Enable Template'}
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Template Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    maxLength={255}
                    disabled={isAnySaving}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Category *</Form.Label>
                  <Form.Control
                    type="text"
                    value={templateCategory}
                    onChange={(e) => setTemplateCategory(e.target.value)}
                    maxLength={100}
                    disabled={isAnySaving}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    maxLength={2000}
                    disabled={isAnySaving}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                {finalReviewConfigured && (
                  <Alert variant="warning" className="d-flex align-items-center gap-2 py-2 mb-2">
                    <strong>Final review is active</strong> — this template will route to a final reviewer when all required steps are complete.
                    {missingFinalSteps && (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        className="ms-auto text-nowrap"
                        onClick={addFinalReviewSteps}
                        disabled={isAnySaving}
                      >
                        <FaPlus className="me-1" />
                        Add final review steps
                      </Button>
                    )}
                    {!missingFinalSteps && (
                      <span className="ms-auto text-success fw-semibold small">✓ Final review steps present</span>
                    )}
                  </Alert>
                )}
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Final Reviewer Email (optional)</Form.Label>
                      <Form.Control
                        type="email"
                        value={finalReviewAssigneeEmail}
                        onChange={(e) => setFinalReviewAssigneeEmail(e.target.value)}
                        placeholder="review.admin@enmax.com"
                        disabled={isAnySaving}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Final Reviewer Role (optional)</Form.Label>
                      <Form.Control
                        type="text"
                        value={finalReviewRole}
                        onChange={(e) => setFinalReviewRole(e.target.value)}
                        placeholder="Admin"
                        disabled={isAnySaving}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Text className="text-muted">
                  These fields save into template config under finalReview.assigneeEmail and finalReview.role.
                </Form.Text>
              </Col>
              {showAdvanced && (
                <>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Report Catalog Picker</Form.Label>
                      <Row className="g-2">
                        <Col md={6}>
                          <Form.Select
                            value={selectedCatalogReportId}
                            onChange={(e) => {
                              const val = e.target.value;
                              setSelectedCatalogReportId(val);
                              const report = availableReports.find((r) => r.m365ReportId === Number(val));
                              if (report) {
                                setSelectedCatalogReportKey(slugifyCatalogKey(report.reportTitle));
                              }
                            }}
                            disabled={isAnySaving}
                          >
                            <option value="">Select report from M365Reports...</option>
                            {availableReports.map((report) => (
                              <option key={report.m365ReportId} value={report.m365ReportId}>
                                {report.reportTitle} (ID: {report.m365ReportId})
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={4}>
                          <Form.Control
                            type="text"
                            value={selectedCatalogReportKey}
                            onChange={(e) => setSelectedCatalogReportKey(e.target.value)}
                            placeholder="catalogKey"
                            disabled={isAnySaving}
                          />
                        </Col>
                        <Col md={2}>
                          <Button
                            type="button"
                            variant="outline-primary"
                            className="w-100"
                            onClick={addSelectedReportToCatalog}
                            disabled={isAnySaving || !selectedCatalogReportId}
                          >
                            Add
                          </Button>
                        </Col>
                        <Col md={12}>
                          <Form.Check
                            type="checkbox"
                            id="catalog-auto-queue-on-assign"
                            label="Auto-queue on assign"
                            checked={catalogAutoQueueOnAssign}
                            onChange={(e) => setCatalogAutoQueueOnAssign(e.target.checked)}
                            disabled={isAnySaving}
                          />
                          <Form.Text className="text-muted">
                            Automatically queue this report when a review from this template is assigned.
                          </Form.Text>
                        </Col>
                      </Row>
                      <Form.Text className="text-muted">
                        Pick a report, set a catalog key, and add it. JSON is auto-populated and still editable below.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Report Catalog (JSON, optional)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        value={reportCatalogConfig}
                        onChange={(e) => setReportCatalogConfig(e.target.value)}
                        placeholder={`{
  "siteInventory": {
    "m365ReportId": 22,
    "title": "Generate Site Inventory Report",
    "defaultOptions": { "debug": false, "debugLevel": 0 }
  },
  "userAccess": {
    "m365ReportId": 23,
    "title": "Generate User Access List"
  }
}`}
                        disabled={isAnySaving}
                      />
                      <Form.Text className="text-muted">
                        Saves under reportCatalog.* in template config. Report-trigger steps can reference keys from this catalog.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Card className="border-light-subtle">
                      <Card.Body>
                        <div className="fw-semibold mb-2">Escalation Follow-up (optional)</div>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Trigger Decision Outcome</Form.Label>
                              <Form.Control
                                type="text"
                                value={escalationTriggerDecision}
                                onChange={(e) => setEscalationTriggerDecision(e.target.value)}
                                placeholder="Further review required"
                                disabled={isAnySaving}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Follow-up Review Status</Form.Label>
                              <Form.Control
                                type="text"
                                value={escalationStatusOnEscalation}
                                onChange={(e) => setEscalationStatusOnEscalation(e.target.value)}
                                placeholder="Escalated"
                                disabled={isAnySaving}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Follow-up Template Guid</Form.Label>
                              <Form.Control
                                type="text"
                                value={escalationFollowUpTemplateGuid}
                                onChange={(e) => setEscalationFollowUpTemplateGuid(e.target.value)}
                                placeholder="00000000-0000-0000-0000-000000000000"
                                disabled={isAnySaving}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Follow-up Review Name Suffix</Form.Label>
                              <Form.Control
                                type="text"
                                value={escalationFollowUpReviewName}
                                onChange={(e) => setEscalationFollowUpReviewName(e.target.value)}
                                placeholder="Records Content Review"
                                disabled={isAnySaving}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Escalation Assignee Email</Form.Label>
                              <Form.Control
                                type="email"
                                value={escalationAssigneeEmail}
                                onChange={(e) => setEscalationAssigneeEmail(e.target.value)}
                                placeholder="records.team@enmax.com"
                                disabled={isAnySaving}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Escalation Assignee Role</Form.Label>
                              <Form.Control
                                type="text"
                                value={escalationAssigneeRole}
                                onChange={(e) => setEscalationAssigneeRole(e.target.value)}
                                placeholder="RecordsManagement"
                                disabled={isAnySaving}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Notification Security Group Id</Form.Label>
                              <Form.Control
                                type="text"
                                value={escalationNotificationGroupId}
                                onChange={(e) => setEscalationNotificationGroupId(e.target.value)}
                                placeholder="15cc4bed-3c1c-4ea9-8309-44ab24797d3b"
                                disabled={isAnySaving}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Notification Security Group Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={escalationNotificationGroupName}
                                onChange={(e) => setEscalationNotificationGroupName(e.target.value)}
                                placeholder="SG - Records Management"
                                disabled={isAnySaving}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Text className="text-muted d-block mt-2">
                          Saves under escalation.* in template config. If trigger decision matches and follow-up template is set,
                          the current review is completed with the escalation status and a linked follow-up review is auto-created.
                          Notification Security Group members are automatically CC'd on assignment.
                        </Form.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Template Config (JSON)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={reviewTemplateConfig}
                        onChange={(e) => setReviewTemplateConfig(e.target.value)}
                        placeholder='{"version":1,"assignForm":{"title":"...","fields":[...]},"finalReview":{"assigneeEmail":"review.admin@enmax.com","role":"Admin"},"escalation":{"triggerDecision":"Further review required","followUpTemplateGuid":"...","followUpReviewName":"Records Content Review","assigneeRole":"RecordsManagement","notificationGroupId":"15cc4bed-3c1c-4ea9-8309-44ab24797d3b","notificationGroupName":"SG - Records Management","statusOnEscalation":"Escalated"}}'
                        disabled={isAnySaving}
                      />
                      <Form.Text className="text-muted">
                        Optional JSON schema used to render template-specific assign form fields plus final review and escalation routing.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </>
              )}
              {!showAdvanced && (
                <Col md={12}>
                  <Alert variant="light" className="mb-0">
                    Advanced settings are hidden. Use <strong>Show Advanced</strong> to manage report catalog, escalation, and raw template JSON.
                  </Alert>
                </Col>
              )}
            </Row>
            <div className="mt-3 small text-muted">
              Status: <strong>{isActive ? 'Active' : 'Disabled'}</strong>
            </div>
          </Card.Body>
          {isEdit && (
            <Card.Footer className="d-flex justify-content-end">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={isAnySaving || !templateName.trim() || !templateCategory.trim()}
              >
                <FaSave className="me-2" />
                {detailsSaving ? 'Saving…' : 'Save Details'}
              </Button>
            </Card.Footer>
          )}
        </Card>

        {/* ---- Steps Card ---- */}
        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>Template Steps</strong>
            <div className="d-flex gap-2">
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={expandAllSteps}
                disabled={isAnySaving}
              >
                <FaChevronUp className="me-1" />
                Expand All
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={collapseAllSteps}
                disabled={isAnySaving}
              >
                <FaChevronDown className="me-1" />
                Collapse All
              </Button>
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                onClick={addStep}
                disabled={isAnySaving}
              >
                <FaPlus className="me-1" />
                Add Step
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {steps.map((step, index) => {
              const dirty = isEdit && isDirtyStep(index);
              const isSavingThis = savingStepIdx === index;
              const isExpanded = expandedSteps[index] ?? true;
              const stepTitle = step.stepName.trim() || '(Untitled step)';
              const parsedStepConfig = tryParseStepConfigObject(step.stepConfig);
              const isReportTrigger = String(parsedStepConfig.type ?? '').toLowerCase() === 'reporttrigger';
              const reportKeysCsv = Array.isArray(parsedStepConfig.reports)
                ? (parsedStepConfig.reports as unknown[])
                    .filter((v): v is string => typeof v === 'string' && !!v.trim())
                    .join(', ')
                : '';

              return (
                <Card
                  key={index}
                  className={`mb-3 ${dirty ? 'border-warning' : 'border-light-subtle'}`}
                >
                  <Card.Header 
                    className="d-flex justify-content-between align-items-center"
                    style={{ cursor: isSavingThis ? 'default' : 'pointer' }}
                    onClick={() => !isSavingThis && toggleStepExpanded(index)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <strong>Step {index + 1}</strong>
                      <span className="text-start">
                        {stepTitle}
                      </span>
                      {dirty && (
                        <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>
                          unsaved
                        </span>
                      )}
                    </div>
                    {/* Up/Down/Remove shown only when step is clean */}
                    {!dirty && (
                      <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => moveStep(index, -1)}
                          disabled={index === 0 || orderSaving}
                          title="Move step up"
                        >
                          <FaArrowUp />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => moveStep(index, 1)}
                          disabled={index === steps.length - 1 || orderSaving}
                          title="Move step down"
                        >
                          <FaArrowDown />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-danger"
                          onClick={() => removeStep(index)}
                          disabled={steps.length === 1 || orderSaving}
                          title="Remove step"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    )}
                  </Card.Header>

                  <Collapse in={isExpanded}>
                    <div>
                      <Card.Body>
                        {/* Fields */}
                        <Row className="g-2">
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>Step Name *</Form.Label>
                              <Form.Control
                                type="text"
                                value={step.stepName}
                                onChange={(e) => updateStep(index, { stepName: e.target.value })}
                                maxLength={500}
                                disabled={isSavingThis}
                                required={!isEdit}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>Description</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={step.stepDescription}
                                onChange={(e) => updateStep(index, { stepDescription: e.target.value })}
                                disabled={isSavingThis}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <div className="d-flex gap-2 mb-2 flex-wrap">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                onClick={() => setDecisionStepPreset(index)}
                                disabled={isSavingThis}
                              >
                                Use Decision Step
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline-info"
                                onClick={() => setReportTriggerStepPreset(index)}
                                disabled={isSavingThis}
                              >
                                Use Report Trigger Step
                              </Button>
                            </div>

                            {isReportTrigger && (
                              <Form.Group className="mb-2">
                                <Form.Label>Report Catalog Keys (comma-separated)</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={reportKeysCsv}
                                  onChange={(e) => setReportTriggerKeys(index, e.target.value)}
                                  placeholder="siteInventory, userAccess, sharingLinks"
                                  disabled={isSavingThis}
                                />
                                <Form.Text className="text-muted">
                                  Keys should match entries in template reportCatalog.
                                </Form.Text>
                              </Form.Group>
                            )}

                            <Form.Group>
                              <Form.Label>Config (optional)</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={step.stepConfig}
                                onChange={(e) => updateStep(index, { stepConfig: e.target.value })}
                                disabled={isSavingThis}
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <div className="d-flex gap-3 mt-2">
                          <Form.Check
                            type="checkbox"
                            label="Required"
                            checked={step.isRequired}
                            onChange={(e) => updateStep(index, { isRequired: e.target.checked })}
                            disabled={isSavingThis}
                          />
                          <Form.Check
                            type="checkbox"
                            label="Active"
                            checked={step.isActive}
                            onChange={(e) => updateStep(index, { isActive: e.target.checked })}
                            disabled={isSavingThis}
                          />
                        </div>

                        {/* Inline Save / Cancel — edit mode only, when step is dirty */}
                        {isEdit && dirty && (
                          <div className="d-flex justify-content-end gap-2 mt-3 pt-2 border-top">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => cancelStepEdit(index)}
                              disabled={isSavingThis}
                            >
                              <FaTimesCircle className="me-1" />
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="success"
                              onClick={() => saveStepEdit(index)}
                              disabled={isSavingThis || !step.stepName.trim()}
                            >
                              <FaSave className="me-1" />
                              {isSavingThis ? 'Saving…' : 'Save Step'}
                            </Button>
                          </div>
                        )}
                      </Card.Body>
                    </div>
                  </Collapse>
                </Card>
              );
            })}
          </Card.Body>
        </Card>

        {/* Create mode — bottom submit */}
        {!isEdit && (
          <div className="d-flex justify-content-end gap-2 mb-4">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => navigate('/admin/assign-review')}
              disabled={detailsSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              disabled={
                detailsSaving ||
                !templateName.trim() ||
                !templateCategory.trim() ||
                steps.length === 0 ||
                steps.some((s) => !s.stepName.trim())
              }
            >
              {detailsSaving ? 'Creating…' : 'Create Template'}
            </Button>
          </div>
        )}
      </Form>

      <Modal show={showDisableConfirm} onHide={() => setShowDisableConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Disable template?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-0">
            There are {activeReviewCount} active review(s) using this template.
            Disabling the template will not cancel those reviews automatically.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDisableConfirm(false)}>
            Keep Active
          </Button>
          <Button variant="danger" onClick={confirmDisableTemplate}>
            Disable Template
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Unsaved Changes Warning */}
      <Modal show={showUnsavedWarning} onHide={() => setShowUnsavedWarning(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-0">
            You have unsaved changes. Preview will show your current edits, but they won't be saved unless you explicitly save them.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUnsavedWarning(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={proceedWithPreview}>
            Preview Anyway
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Template Preview Modal */}
      <TemplatePreviewModal 
        show={showPreview} 
        onHide={() => setShowPreview(false)}
        template={getPreviewData()}
      />
    </Container>
  );
};

export default ReviewTemplateEditor;

